import { FormEvent, useState, type CSSProperties } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import api from '../../services/api';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { logout, passwordChanged } from '../../features/auth/authSlice';

const FIELD: CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 10,
  border: '1.5px solid #f9c0d8',
  fontSize: 14,
  background: '#fff5f8',
  color: '#6b0030',
  outline: 'none',
  boxSizing: 'border-box',
};

const BTN: CSSProperties = {
  width: '100%',
  padding: '11px 0',
  borderRadius: 12,
  fontWeight: 700,
  fontSize: 14,
  background: '#ffffff',
  color: '#d6006e',
  border: '2px solid #d6006e',
  boxShadow: '0 4px 16px rgba(212,0,106,0.14)',
  cursor: 'pointer',
};

const LABEL = 'mb-1 block text-xs font-semibold text-[#9d0050]';

export default function SetPasswordPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { token, user } = useAppSelector((s) => s.auth);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) return <Navigate to="/login" replace />;
  if (!user?.mustChangePassword) return <Navigate to="/" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirm) {
      setError('The new passwords do not match.');
      return;
    }
    if (newPassword.length < 10 || !/[A-Za-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError('Use at least 10 characters with at least one letter and one number.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      dispatch(passwordChanged());
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { message?: string } } })?.response;
      if (res?.status === 401) {
        dispatch(logout());
        return;
      }
      setError(res?.data?.message || 'Unable to reach Micus Workspace. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center px-6 py-10"
      style={{ background: 'linear-gradient(145deg, #1a000f 0%, #7a0038 35%, #d6006e 70%, #ff4fa2 100%)' }}
    >
      <div className="w-full max-w-[400px] mz-fade-up">
        <div className="mb-5 flex items-center gap-3">
          <BrandLogo size={34} className="brightness-0 invert" />
          <div>
            <p className="text-lg font-black tracking-tight text-white">Micus</p>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/70">Workspace</p>
          </div>
        </div>

        <div
          className="rounded-2xl p-6 sm:p-7"
          style={{
            background: 'rgba(255,255,255,0.96)',
            boxShadow: '0 24px 60px rgba(212,0,106,0.22), 0 8px 20px rgba(0,0,0,0.1)',
          }}
        >
          <p className="text-xs font-bold uppercase tracking-widest text-[#d6006e]">First sign-in</p>
          <h1 className="mt-1 text-xl font-black tracking-tight text-[#6b0030]">Set your own password</h1>
          <p className="mt-1 text-sm text-[#c084a0]">
            Replace the temporary app password from your welcome email to secure <strong>{user.email}</strong>.
          </p>

          {error && (
            <div
              role="alert"
              className="mt-4 rounded-xl px-3.5 py-2.5 text-sm"
              style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#be123c' }}
            >
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-4 space-y-3.5">
            <input type="email" autoComplete="username" value={user.email} readOnly hidden />
            <div>
              <label className={LABEL} htmlFor="sp-current">Temporary app password</label>
              <input
                id="sp-current"
                type={show ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mz-field"
                style={FIELD}
              />
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-semibold text-[#9d0050]" htmlFor="sp-new">New password</label>
                <button type="button" onClick={() => setShow((v) => !v)} className="text-xs font-semibold text-[#d6006e]">
                  {show ? 'Hide' : 'Show'}
                </button>
              </div>
              <input
                id="sp-new"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mz-field"
                style={FIELD}
              />
              <p className="mt-1 text-[11px] text-[#c084a0]">At least 10 characters, with a letter and a number.</p>
            </div>
            <div>
              <label className={LABEL} htmlFor="sp-confirm">Confirm new password</label>
              <input
                id="sp-confirm"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mz-field"
                style={FIELD}
              />
            </div>
            <button type="submit" disabled={loading} className="mz-cta" style={BTN}>
              {loading ? 'Saving…' : 'Save password & continue →'}
            </button>
          </form>

          <button
            type="button"
            onClick={() => dispatch(logout())}
            className="mt-4 w-full text-center text-xs font-semibold text-[#c084a0]"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
